import React, { ReactNode, useLayoutEffect, useState } from "react";
import { Button, Form, Select, Table } from "antd";
import classes from "./BusinessRisks.module.css";

import { useGetApplicationDataToBankQuery } from "../../../../../store/api/applicationBankApi/applicationBank.api";
import { useParams } from "react-router-dom";
import { ruleRequired } from "../../../../../constants/rules/RulesConstantsForms";
import { BusinessRisk } from "../../../../../store/types/scoring.type";
import { selectOptionsParams } from "./config";
import { ColumnsType } from "antd/es/table";
import { CommentForm } from "../../../../../UI_ITO/UI_ITOModuleCommentForm/CommentForm";
import { APPLICATION_BANK_STATUS } from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { UI_ITOModuleSuccess } from "../../../../../UI_ITO/indexUI";
import { Guard } from "../../../../../utils/guards/Guard";

import { isEmpty } from "lodash";
import {
  BusinessRisksChange,
  useChangeDataToScoringMutation,
  useSendDataToScoringMutation,
} from "../../../../../store/api/applicationBankApi/scoring.api";

// Нижняя строка таблицы - подсчёт итогов
const SummaryTableRow = (tableData: readonly BusinessRisk[]) => {
  let totalValue = 0;

  tableData.forEach(({ value }) => {
    totalValue += Number(value);
  });

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}></Table.Summary.Cell>
      <Table.Summary.Cell index={1}></Table.Summary.Cell>
      <Table.Summary.Cell index={2}>Итого</Table.Summary.Cell>
      <Table.Summary.Cell index={4}>{totalValue}</Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

export const BusinessRisks = () => {
  const { applicationBankId } = useParams();
  const [businessRisksForm] = Form.useForm();
  const [sendDataToScoring] = useSendDataToScoringMutation();

  const { data: applicationBank } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );

  // Переводим значения value в number | null
  const dataBusinessRisks =
    applicationBank?.scoring_result?.data?.businessRisks.map((el) => {
      const isValue =
        el.value !== undefined &&
        el.value !== null &&
        selectOptionsParams[el.key].some((option) => option.value == el.value);

      return { ...el, value: isValue ? Number(el.value) : null };
    });

  // Используем state для перерендеринга таблицы
  const [tableData, setTableData] = useState<BusinessRisk[]>(
    dataBusinessRisks || []
  );

  // комментарий
  const commentBusinessRisks =
    applicationBank?.scoring_result?.data?.commentBusinessRisks;

  // Обновляем данные при изменении данных с бэкенда
  useLayoutEffect(() => {
    if (applicationBank) {
      businessRisksForm.resetFields();
      dataBusinessRisks && setTableData(dataBusinessRisks);
    }
  }, [applicationBank, businessRisksForm]);

  const [changeData, { isSuccess, isLoading }] =
    useChangeDataToScoringMutation();

  // Обновляем данные при изменении селекторов
  const handleChangeValue = (row: BusinessRisk, index: number) => {
    if (tableData) {
      // подставляем новое значение в стейт
      setTableData((prev) => {
        return prev?.map((prevRow) => {
          return prevRow.key === row.key ? row : prevRow;
        });
      });
      // Подставляем значение в форму
      businessRisksForm.setFieldValue([index], row);
    }
  };

  const columns: ColumnsType<BusinessRisk> = [
    {
      title: "№",
      dataIndex: "key",
      width: "5%",
      key: "key",
      render: (value: string, record): ReactNode => (
        <Form.Item
          style={{ margin: 0, width: "100%", padding: 0 }}
          name={[record.key, "key"]}
          noStyle
        >
          {value}
        </Form.Item>
      ),
    },
    {
      title: "Наименование показателя",
      dataIndex: "title",
      width: "50%",
      key: "title",
    },
    {
      title: "Показатели",
      dataIndex: "value",
      width: "30%",
      key: "value",
      render: (_value: number, record, index): ReactNode => {
        return (
          <Form.Item
            style={{ margin: 0, width: "270px", padding: 0 }}
            name={[index, "value"]}
            rules={[ruleRequired]}
          >
            <Select
              onChange={(value) =>
                handleChangeValue(
                  {
                    ...record,
                    value: value,
                  } as BusinessRisk,
                  index
                )
              }
              style={{ height: "auto" }}
            >
              {selectOptionsParams[record.key]?.map((option) => {
                return (
                  <Select.Option value={option.value} key={option.title}>
                    <div className={classes["select-option-row"]}>
                      {option.title}
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        );
      },
    },
    {
      title: "Баллы",
      dataIndex: "value",
      width: "10%",
      key: "value",
    },
  ];

  const handleFinish = async () => {
    const values = businessRisksForm.getFieldsValue(true);
    // Формируем массив для отправки данных на бэк
    const formData = Object.keys(values)?.map((item) => {
      return {
        key: values[item].key,
        value: +values[item].value ?? 0,
      };
    });

    // Убираем последнее поле - "Дополнительный комментарий"
    formData.pop();
    const data = {
      type: "businessRisks",
      commentBusinessRisks: String(values["commentBusinessRisks"]) ?? "",
      results: formData,
    };

    if (applicationBankId) {
      await changeData({
        app_id: +applicationBankId,
        data: { ...(data as BusinessRisksChange) },
      });
      await sendDataToScoring({ app_id: +applicationBankId });
    }
  };

  const initialValues = {
    ...dataBusinessRisks,
    commentBusinessRisks: commentBusinessRisks || "",
  };

  return (
    <>
      <Form
        form={businessRisksForm}
        name={"businessRisksForm"}
        onFinish={handleFinish}
        initialValues={initialValues}
        autoComplete="off"
        scrollToFirstError={{ behavior: "smooth", block: "end" }}
      >
        {!isEmpty(tableData) && (
          <Table
            style={{ whiteSpace: "pre-line" }}
            dataSource={tableData}
            rowClassName={() => "editable-row"}
            bordered
            pagination={false}
            columns={columns}
            summary={SummaryTableRow}
          />
        )}

        <CommentForm
          name="commentBusinessRisks"
          disabled={
            applicationBank?.modified_bank_status.type ===
            APPLICATION_BANK_STATUS.NEW_APPLICATION
          }
        />

        <div className={classes.submitWrapper}>
          <UI_ITOModuleSuccess
            isSuccess={isSuccess}
            title="Данные успешно сохранены"
          />
          {!!applicationBank && (
            <Guard.ApplicationBankStatus
              status={applicationBank.modified_bank_status.type}
              disabledStatuses={[APPLICATION_BANK_STATUS.NEW_APPLICATION]}
            >
              <Button htmlType="submit" type="primary" loading={isLoading}>
                Сохранить и отправить на доработку скоринга
              </Button>
            </Guard.ApplicationBankStatus>
          )}
        </div>
      </Form>
    </>
  );
};
